import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LogPanel, type LogLine } from "./LogPanel";
import { useSSEAppend } from "../hooks/useSSEAppend";

/**
 * Per-device activity feed. Renders a `<LogPanel />` of recent activity for a
 * single device, live-updating via SSE.
 *
 * Data flow:
 *   1. On mount, fetch /api/devices/:id/activity?limit=200 (sorted ts DESC).
 *   2. Subscribe to SSE event `device:activity`; filter to events for this
 *      `deviceId` and prepend (newest first) to local state.
 *   3. Search/level filters re-fetch from the server (debounced).
 *   4. Pause toggle stops SSE appends — useful while investigating a moment.
 *
 * Order: the local list is kept ts DESC, id DESC. Matches the backend
 * `readDeviceActivity` order so server-fetched and SSE-appended entries are
 * consistent.
 */

interface ActivityEntry {
  id: string;
  device_id: string;
  ts: string;
  kind: string;
  level: "info" | "warn" | "error";
  message: string;
  meta: Record<string, unknown> | null;
}

interface ActivityResponse {
  device_id: string;
  lines: ActivityEntry[];
}

const DEFAULT_LIMIT = 200;
const SEARCH_DEBOUNCE_MS = 300;

export function DeviceActivityFeed({ deviceId }: { deviceId: string }) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState<"" | "info" | "warn" | "error">("");
  const [paused, setPaused] = useState(false);

  // Debounce the search input — we don't want a server hit on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(DEFAULT_LIMIT) });
      if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
      if (levelFilter) params.set("level", levelFilter);

      const res = await fetch(`/api/devices/${deviceId}/activity?${params}`, {
        credentials: "include"
      });
      if (res.status === 401) {
        window.location.href = "/auth/login";
        return;
      }
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const json = (await res.json()) as ActivityResponse;
      // Server already returns ts DESC, id DESC — preserve as-is.
      setEntries(json.lines);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [deviceId, debouncedSearch, levelFilter]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  // Live append from SSE. Filter to events matching this deviceId, dedupe
  // by id (an event can arrive both via local fan-out and pg pubsub on the
  // same pod — broadcaster.broadcast() handles that, but defense-in-depth),
  // and cap the local list so unbounded streaming doesn't OOM the tab.
  const seenIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    seenIdsRef.current = new Set(entries.map((e) => e.id));
  }, [entries]);

  // Apply current filters in-memory to incoming SSE entries — without this,
  // the search would only filter the initial fetch, not new events.
  const matchesFilters = useCallback(
    (e: ActivityEntry): boolean => {
      if (levelFilter) {
        const rank = { info: 0, warn: 1, error: 2 } as const;
        if (rank[e.level] < rank[levelFilter]) return false;
      }
      if (debouncedSearch.trim()) {
        if (!e.message.toLowerCase().includes(debouncedSearch.trim().toLowerCase())) {
          return false;
        }
      }
      return true;
    },
    [levelFilter, debouncedSearch]
  );

  useSSEAppend<ActivityEntry>(
    "/api/events",
    "device:activity",
    (payload) => {
      if (!payload || payload.device_id !== deviceId) return;
      if (seenIdsRef.current.has(payload.id)) return;
      if (!matchesFilters(payload)) return;
      seenIdsRef.current.add(payload.id);
      setEntries((prev) => {
        // Newest first to match server order. Insert by ts DESC, id DESC,
        // then cap at DEFAULT_LIMIT so the tab doesn't grow forever.
        const next = [payload, ...prev];
        next.sort((a, b) => {
          const tDiff = Date.parse(b.ts) - Date.parse(a.ts);
          if (tDiff !== 0) return tDiff;
          return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
        });
        if (next.length > DEFAULT_LIMIT) next.length = DEFAULT_LIMIT;
        return next;
      });
    },
    !paused
  );

  // LogPanel wants oldest-first because it auto-scrolls to bottom for live
  // tail. Our server returns newest-first, so flip it for display. Keep
  // server order for everything else (filtering, dedup) — only flip at the
  // render boundary.
  const displayLines: LogLine[] = useMemo(() => {
    const reversed = [...entries].reverse();
    return reversed.map((e) => ({
      seq: undefined,
      ts: e.ts,
      level: e.level,
      source: e.kind,
      line: e.message
    }));
  }, [entries]);

  const subtitleParts = [
    `${entries.length} entries`,
    paused ? "PAUSED" : "live",
    debouncedSearch.trim() ? `q="${debouncedSearch.trim()}"` : null,
    levelFilter ? `≥${levelFilter}` : null
  ].filter(Boolean);

  return (
    <div style={{ marginTop: "var(--space-xl)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-md)",
          marginBottom: 6,
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-xs)",
          flexWrap: "wrap"
        }}
      >
        <span style={{ flex: 1 }}>Activity feed · {subtitleParts.join(" · ")}</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search messages…"
          aria-label="Search activity"
          style={{
            padding: "4px 8px",
            fontSize: "var(--font-size-xs)",
            border: "var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-foreground)",
            outline: "none",
            width: 200
          }}
        />
        <select
          value={levelFilter}
          onChange={(e) =>
            setLevelFilter(e.target.value as "" | "info" | "warn" | "error")
          }
          aria-label="Minimum level"
          style={{
            padding: "4px 8px",
            fontSize: "var(--font-size-xs)",
            border: "var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-foreground)"
          }}
        >
          <option value="">All levels</option>
          <option value="info">Info+</option>
          <option value="warn">Warn+</option>
          <option value="error">Error</option>
        </select>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer"
          }}
        >
          <input
            type="checkbox"
            checked={paused}
            onChange={(e) => setPaused(e.target.checked)}
          />
          Pause live
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            color: "var(--color-danger)",
            fontSize: "var(--font-size-sm)",
            marginBottom: 4
          }}
        >
          {error}
        </div>
      )}

      <LogPanel
        title="Device activity"
        live={!paused}
        // Disable LogPanel's own client-side filter UI — we filter on the
        // server (and on incoming SSE events) so the panel just renders.
        filterable={false}
        lines={displayLines}
        emptyHint={
          loading
            ? "Loading…"
            : debouncedSearch.trim() || levelFilter
              ? "No entries match the current filter."
              : "No activity in the last 24 hours."
        }
        height={360}
        maxLines={DEFAULT_LIMIT}
      />
    </div>
  );
}
